import BLOCK_GAP from 'src/constants/blockGap';
import { Block, Edge } from 'src/types/shared/blocksV3';
import BlockType from 'src/enums/blockType';
import EdgeType from 'src/enums/edgeType';
import { getOppositeEdge } from './edgeUtils';
import getBlockChildren from 'src/utils/getBlockChildrenV3';
import getBlock from 'src/utils/getBlockV3';

export function getBlockUpdatedPosition(
	parentBlock: Block,
	block: Block,
	scaleFactor: number
) {
	if (!parentBlock || !block) {
		return {
			x: block.position?.x || 0,
			y: block.position?.y || 0,
		};
	}

	if (block.type == BlockType.BUMPOUT)
		return getBumpoutUpdatedPosition(parentBlock, block, scaleFactor);

	const edgeAttachedToParent = block.edges.find(
		(edge) => edge.attachedToParent
	);

	if (!edgeAttachedToParent) {
		return {
			x: block.position?.x || 0,
			y: block.position?.y || 0,
		};
	}

	// Calculate segment offset - find which segment the block is attached to
	const edgeAttachedToChild = parentBlock.edges.find(
		(edge) => edge.label == getOppositeEdge(edgeAttachedToParent?.label)
	);

	// Find the segment index - use ?? -1 to properly handle findIndex returning -1
	const attachedSegmentIndex =
		edgeAttachedToChild?.segments.findIndex(
			(segment) => segment.id == block.parentBlock?.segmentId
		) ?? -1;

	// Calculate offset with proper validation
	let blockOffset = 0;
	if (attachedSegmentIndex === -1 || !edgeAttachedToChild) {
		if (block.type === BlockType.LANDING || block.type === BlockType.STAIR) {
			console.log('[DebugPosition] Failed for', block.type, block.id, {
				parentId: parentBlock.id,
				parentType: parentBlock.type,
				attachedEdgeLabel: edgeAttachedToParent?.label,
				lookingForOpposite: getOppositeEdge(edgeAttachedToParent?.label),
				foundParentEdge: edgeAttachedToChild?.label,
				segmentId: block.parentBlock?.segmentId,
				parentSegments: edgeAttachedToChild?.segments?.map((s) => s.id),
			});
		}
		// Segment not found or edge not found - log warning and use offset 0
		console.warn('[getBlockUpdatedPosition] Segment not found:', {
			blockId: block.id,
			blockLabel: block.label,
			blockType: block.type,
			parentId: parentBlock.id,
			parentLabel: parentBlock.label,
			segmentId: block.parentBlock?.segmentId,
			edgeLabel: edgeAttachedToParent?.label,
			oppositeEdge: getOppositeEdge(edgeAttachedToParent?.label),
			foundEdge: edgeAttachedToChild?.label,
			availableSegments: edgeAttachedToChild?.segments?.map((s) => ({
				id: s.id,
				length: s.length,
			})),
		});
	} else if (attachedSegmentIndex > 0) {
		// Calculate cumulative length of segments before the attached segment
		blockOffset = edgeAttachedToChild.segments
			.slice(0, attachedSegmentIndex)
			.reduce((acc, segment) => acc + segment.length, 0);
	}

	// Structural attachments (Stair->Landing, Landing->Stair) should abut without a gap.
	// Only add BLOCK_GAP for loose attachments or other types.
	const isStructuralAttachment =
		edgeAttachedToParent?.type === EdgeType.BLOCK_ATTACHED ||
		edgeAttachedToParent?.type === EdgeType.STAIR_ATTACHED ||
		edgeAttachedToParent?.type === EdgeType.LANDING_ATTACHED ||
		edgeAttachedToParent?.segments.some(
			(s) =>
				s.type === EdgeType.BLOCK_ATTACHED ||
				s.type === EdgeType.STAIR_ATTACHED ||
				s.type === EdgeType.LANDING_ATTACHED
		);

	// Explicitly force 0 gap for Landings attached to blocks, just in case
	const isLandingAttached =
		block.type === BlockType.LANDING &&
		edgeAttachedToParent?.type === EdgeType.BLOCK_ATTACHED;
	const effectiveGap =
		isStructuralAttachment || isLandingAttached ? 0 : BLOCK_GAP;

	let position;

	switch (edgeAttachedToParent?.label) {
		case 'Top':
			position = {
				x: (parentBlock.position?.x || 0) + blockOffset * scaleFactor,
				y:
					(parentBlock.position?.y || 0) +
					parentBlock.displayDimensions.height * scaleFactor +
					effectiveGap,
			};
			break;
		case 'Bottom':
			position = {
				x: (parentBlock.position?.x || 0) + blockOffset * scaleFactor,
				y:
					(parentBlock.position?.y || 0) -
					block.displayDimensions.height * scaleFactor -
					effectiveGap,
			};
			break;
		case 'Left':
			position = {
				x:
					(parentBlock.position?.x || 0) +
					parentBlock.displayDimensions.width * scaleFactor +
					effectiveGap,
				y: (parentBlock.position?.y || 0) + blockOffset * scaleFactor,
			};
			break;
		case 'Right':
			position = {
				x:
					(parentBlock.position?.x || 0) -
					block.displayDimensions.width * scaleFactor -
					effectiveGap,
				y: (parentBlock.position?.y || 0) + blockOffset * scaleFactor,
			};
			break;
	}

	return position;
}

export function getEdgeChildrenUpdatedPositions(
	blocks: Block[],
	blockId: string | undefined,
	edgeId: string | undefined,
	scaleFactor: number
) {
	if (!blockId || !edgeId) {
		return [];
	}

	const block = getBlock(blocks, blockId);

	if (!block) {
		return [];
	}

	const edge = block.edges.find((ed) => ed.id == edgeId);

	const edgeChildren = getBlockChildren(blocks, block).filter(
		(blk) => blk.parentBlock.edgeId == edgeId
	);

	for (const child of edgeChildren) {
		const attachedSegmentIndex =
			edge?.segments.findIndex(
				(segment) => segment.id == child.parentBlock?.segmentId
			) ?? -1;

		let blockOffset = 0;

		if (attachedSegmentIndex > 0 && edge) {
			blockOffset = edge.segments
				.slice(0, attachedSegmentIndex)
				.reduce((acc, segment) => acc + segment.length, 0);
		}

		let position;

		switch (edge?.label) {
			case 'Bottom':
				position = {
					x: (block.position?.x || 0) + blockOffset * scaleFactor,
					y:
						(block.position?.y || 0) +
						block.displayDimensions.height * scaleFactor +
						BLOCK_GAP,
				};
				break;
			case 'Top':
				position = {
					x: (block.position?.x || 0) + blockOffset * scaleFactor,
					y:
						(block.position?.y || 0) -
						block.displayDimensions.height * scaleFactor -
						BLOCK_GAP,
				};
				break;
			case 'Right':
				position = {
					x:
						(block.position?.x || 0) +
						block.displayDimensions.width * scaleFactor +
						BLOCK_GAP,
					y: (block.position?.y || 0) + blockOffset * scaleFactor,
				};
				break;
			case 'Left':
				position = {
					x:
						(block.position?.x || 0) -
						block.displayDimensions.width * scaleFactor -
						BLOCK_GAP,
					y: (block.position?.y || 0) + blockOffset * scaleFactor,
				};
				break;
		}

		child.position = position;
	}

	const updatedBlocks = blocks.map((blk) => {
		const updatedBlk = edgeChildren.find((child) => child.id == blk.id) || blk;

		if (blk.landings) {
			updatedBlk.landings = updatedBlk.landings?.map((landing) => {
				const updatedLanding =
					edgeChildren.find((child) => child.id == landing.id) || landing;

				return updatedLanding;
			});
		}

		if (blk.stairs) {
			updatedBlk.stairs = updatedBlk.stairs?.map((stair) => {
				const updatedStair =
					edgeChildren.find((child) => child.id == stair.id) || stair;

				return updatedStair;
			});
		}

		return updatedBlk;
	});

	return updatedBlocks;
}

export function getBumpoutUpdatedPosition(
	parentBlock: Block,
	block: Block,
	scaleFactor: number
) {
	const edgeAttachedToParent = block.edges.find(
		(edge) => edge.attachedToParent
	);

	let position;

	switch (edgeAttachedToParent?.label) {
		case 'Top':
			position = {
				x: block.position?.x || 0,
				y: parentBlock.position?.y || 0,
			};
			break;
		case 'Bottom':
			position = {
				x: block.position?.x || 0,
				y:
					(parentBlock.position?.y || 0) +
					parentBlock.displayDimensions.height * scaleFactor -
					block.displayDimensions.height * scaleFactor,
			};
			break;
		case 'Left':
			position = {
				x: parentBlock.position?.x || 0,
				y: block.position?.y || 0,
			};
			break;
		case 'Right':
			position = {
				x:
					(parentBlock.position?.x || 0) +
					parentBlock.displayDimensions.width * scaleFactor -
					block.displayDimensions.width * scaleFactor,
				y: block.position?.y || 0,
			};
			break;
	}

	return position;
}
